import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Check,
  Clock3,
  Eye,
  Info,
  MessageSquare,
  PanelRightClose,
  PanelRightOpen,
  SendHorizontal,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ToastAction } from "@/components/ui/toast";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  RevenueIntelligenceChatDataset,
  useRevenueIntelligenceChat,
} from "@/revenue-intelligence/components/RevenueIntelligenceChatContext";
import { RevenueIntelligenceChatChart } from "@/revenue-intelligence/components/RevenueIntelligenceChatChart";
import {
  RevenueIntelligenceBriefItem,
  RevenueIntelligenceChatBrief,
} from "@/revenue-intelligence/components/RevenueIntelligenceChatBrief";
import {
  answerRevenueIntelligenceActiveSignalDetailFollowUp,
  answerRevenueIntelligenceActiveSignalPriorityFollowUp,
  answerRevenueIntelligenceSemanticPlan,
  answerRevenueIntelligenceQuestion,
  buildRevenueIntelligenceRecordChartDataset,
  getRevenueIntelligenceChatOpening,
  isUnsupportedRevenueIntelligenceChatResponse,
  isRevenueIntelligenceSwedish,
  localizeRevenueIntelligenceApprovedFallback,
  localizeRevenueIntelligenceFollowUps,
  RevenueIntelligenceChatTurnContext,
} from "@/revenue-intelligence/components/revenue-intelligence-chat-intents";
import {
  buildRevenueIntelligenceDeterministicChartPlan,
  doesRevenueIntelligenceQuerySelectNamedRecords,
  getRevenueIntelligenceChartLimitation,
  getRevenueIntelligenceParentRecordIds,
  getRevenueIntelligenceRequestedScopeActions,
  isRevenueIntelligenceChartRequest,
  requestRevenueIntelligenceAnswerComposition,
  requestRevenueIntelligenceSemanticPlan,
  referencesRevenueIntelligenceActiveResult,
  RevenueIntelligencePlannerActiveResult,
  RevenueIntelligenceSemanticPlan,
  toPlannerPreviousPlan,
} from "@/revenue-intelligence/lib/revenue-intelligence-semantic-planner";
import {
  createRevenueIntelligenceBrief,
  getRevenueIntelligenceAnalysisRun,
  listRevenueIntelligenceSavedAnalyses,
  parseAnalysisRunResult,
  parseSavedAnalysisRecipe,
  recordRevenueIntelligenceAnalysisRefresh,
  saveRevenueIntelligenceBriefSnapshot,
  saveRevenueIntelligenceAnalysis,
  SavedAnalysisRow,
} from "@/revenue-intelligence/lib/revenue-intelligence-saved-analyses";
import {
  getRevenueIntelligenceChatAvailabilityMessage,
  getRevenueIntelligenceChatAvailabilityState,
  RevenueIntelligenceChatAvailabilityState,
} from "@/revenue-intelligence/lib/revenue-intelligence-chat-availability";
import { getRevenueIntelligenceChatAccessToken } from "@/revenue-intelligence/lib/revenue-intelligence-chat-session";
import {
  REVENUE_INTELLIGENCE_COMPOSER_TIMEOUT_MS,
  REVENUE_INTELLIGENCE_PLANNER_TIMEOUT_MS,
  runRevenueIntelligenceChatRequest,
} from "@/revenue-intelligence/lib/revenue-intelligence-chat-request";
import { supabaseClient } from "@/lib/supabase/client";
import { useAppStore } from "@/stores";
import { useToast } from "@/components/ui/use-toast";
import { usePostHog } from "@posthog/react";
import { useLocation } from "wouter";
import {
  buildPersistentRevenueIntelligenceSignalRegistration,
  formatPersistentSignalPercentage,
  getPersistentSignalKindLabel,
  listPersistentRevenueIntelligenceSignals,
  REVENUE_INTELLIGENCE_SIGNALS_CHANGED_EVENT,
  RevenueIntelligencePersistentSignal,
  transitionPersistentRevenueIntelligenceSignal,
} from "@/revenue-intelligence/lib/revenue-intelligence-signals";
import {
  buildRevenueIntelligenceContextHref,
  REVENUE_INTELLIGENCE_AGENTS_PATH,
  REVENUE_INTELLIGENCE_HOME_PATH,
} from "@/revenue-intelligence/lib/revenue-intelligence-routes";
import { hasRevenueIntelligenceProductAccess } from "@/revenue-intelligence/lib/revenue-intelligence-product-access";

type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  followUpQuestions?: string[];
  chart?: {
    conclusion: string | null;
    dataset: RevenueIntelligenceChatDataset;
    instruction: string;
    period: string | null;
    plan: RevenueIntelligenceSemanticPlan;
    recordIds: string[];
    scopeActionIds: string[];
  };
  brief?: {
    briefId: string;
    items: RevenueIntelligenceBriefItem[];
    period: string | null;
    title: string;
  };
};

const defaultSuggestedQuestions = [
  "What needs management attention?",
  "What should we do?",
  "Where is the forecast exposed?",
  "Which deals close this quarter?",
];

function createMessageId() {
  return crypto.randomUUID();
}

function getChatActivityLabel(
  stage: "understanding" | "finding" | "calculating" | "drawing" | "preparing" | "saving",
  swedish: boolean,
) {
  const copy = swedish
    ? {
        understanding: "Tolkar din fråga",
        finding: "Söker data",
        calculating: "Gör beräkningar",
        drawing: "Ritar graf",
        preparing: "Förbereder svar",
        saving: "Sparar analys",
      }
    : {
        understanding: "Interpreting your question",
        finding: "Finding data",
        calculating: "Running calculations",
        drawing: "Drawing chart",
        preparing: "Preparing answer",
        saving: "Saving analysis",
      };
  return copy[stage];
}

function getRequestErrorProperties(error: unknown) {
  if (!(error instanceof Error)) return { error_code: "unknown" };
  const typed = error as Error & { code?: unknown; status?: unknown };
  return {
    error_code: typeof typed.code === "string" ? typed.code : typed.name,
    http_status: typeof typed.status === "number" ? typed.status : undefined,
  };
}

function isSaveAnalysisQuestion(question: string) {
  const normalized = question.trim().toLocaleLowerCase();
  return [
    "save analysis",
    "save chart",
    "save graph",
    "save this analysis",
    "save this chart",
    "save this graph",
    "spara analys",
    "spara analysen",
    "spara graf",
    "spara grafen",
    "spara den här analysen",
    "spara den här grafen",
  ].includes(normalized);
}

function isSourceQuestion(question: string) {
  const normalized = question.trim().toLocaleLowerCase();
  return [
    "source",
    "what is the source",
    "what is the source?",
    "show the source",
    "källa",
    "vad är källan",
    "vad är källan?",
    "visa källan",
  ].includes(normalized);
}

function applyPlanFilters(
  dataset: RevenueIntelligenceChatDataset,
  plan: RevenueIntelligenceSemanticPlan,
) {
  if (!plan.filters.length) return dataset;
  return {
    ...dataset,
    rows: dataset.rows.filter(row => (
      plan.filters.every(filter => (
        filter.values.includes(String(row[filter.field] ?? ""))
      ))
    )),
  };
}

function ChatWorkingIndicator({ label }: { label: string | null }) {
  return (
    <div className="mr-8 flex min-h-10 items-center gap-2 rounded-[10px] border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] text-gray-500">
      <span className="flex items-center gap-1" aria-label="Working">
        {[0, 1, 2].map(index => (
          <span
            key={index}
            className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#9F86C0]"
            style={{ animationDelay: `${index * 140}ms` }}
          />
        ))}
      </span>
      {label && <span>{label}</span>}
    </div>
  );
}

function formatSignalTimestamp(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Time unavailable";
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    month: "short",
  }).format(date);
}

interface RevenueIntelligenceChatPanelProps {
  collapsed: boolean;
  mode?: "agents" | "home" | "panel";
  onToggleCollapsed: () => void;
}

export function RevenueIntelligenceChatPanel({
  collapsed,
  mode = "panel",
  onToggleCollapsed,
}: RevenueIntelligenceChatPanelProps) {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [lastContext, setLastContext] = useState<RevenueIntelligenceChatTurnContext | null>(null);
  const [lastSemanticPlan, setLastSemanticPlan] = useState<RevenueIntelligenceSemanticPlan | null>(null);
  const [activeRecordResult, setActiveRecordResult] = useState<RevenueIntelligencePlannerActiveResult | null>(null);
  const [conversationId, setConversationId] = useState(() => crypto.randomUUID());
  const [working, setWorking] = useState(false);
  const [activityLabel, setActivityLabel] = useState<string | null>(null);
  const [availabilityState, setAvailabilityState] = useState<RevenueIntelligenceChatAvailabilityState | null>(null);
  const [savedAnalyses, setSavedAnalyses] = useState<SavedAnalysisRow[]>([]);
  const [savedAnalysesAvailable, setSavedAnalysesAvailable] = useState(true);
  const [selectedSavedAnalysisIds, setSelectedSavedAnalysisIds] = useState<string[]>([]);
  const [homeSignals, setHomeSignals] = useState<RevenueIntelligencePersistentSignal[]>([]);
  const [homeSignalsError, setHomeSignalsError] = useState(false);
  const [homeSignalsLoading, setHomeSignalsLoading] = useState(false);
  const [expandedEvidenceSignalIds, setExpandedEvidenceSignalIds] = useState<Set<string>>(() => new Set());
  const [pendingSignalActionId, setPendingSignalActionId] = useState<string | null>(null);
  const activityTimerRef = useRef<number | null>(null);
  const autoExpandedChartMessageIdsRef = useRef(new Set<string>());
  const [location, setLocation] = useLocation();
  const {
    actorAccessKey,
    accessNotice,
    contracts,
    facts,
    insights,
    metrics,
    recordSets,
    registerContract,
    resolveContractsForQuestion,
    scopeActions,
    suggestedQuestions,
    unregisterContract,
  } = useRevenueIntelligenceChat();
  const contractsRef = useRef(contracts);
  const factsRef = useRef(facts);
  const activeResultContractsRef = useRef<typeof contracts>([]);
  const activeScopeActionIdsRef = useRef<string[]>([]);
  const previousLocationRef = useRef(location);
  const currentUser = useAppStore(state => state.currentUser);
  const { toast } = useToast();
  const posthog = usePostHog();
  const opening = useMemo(
    () => getRevenueIntelligenceChatOpening(insights, facts, metrics, recordSets),
    [facts, insights, metrics, recordSets],
  );

  const homeMode = mode === "home";
  const agentsMode = mode === "agents";
  const hasRevenueIntelligenceAccess = hasRevenueIntelligenceProductAccess(currentUser);
  const canReadCompanySignals = hasRevenueIntelligenceAccess;

  const loadHomeSignals = useCallback(async () => {
    if (!homeMode || !currentUser?.company_id || !canReadCompanySignals) {
      setHomeSignals([]);
      setHomeSignalsError(false);
      setHomeSignalsLoading(false);
      return;
    }
    setHomeSignalsLoading(true);
    setHomeSignalsError(false);
    try {
      const signals = await listPersistentRevenueIntelligenceSignals({
        companyId: String(currentUser.company_id),
      });
      setHomeSignals(signals);
      registerContract(buildPersistentRevenueIntelligenceSignalRegistration({
        companyId: String(currentUser.company_id),
        signals,
      }));
    } catch {
      setHomeSignals([]);
      setHomeSignalsError(true);
      unregisterContract("revenue_intelligence.persistent_signals");
    } finally {
      setHomeSignalsLoading(false);
    }
  }, [
    canReadCompanySignals,
    currentUser?.company_id,
    homeMode,
    registerContract,
    unregisterContract,
  ]);

  useEffect(() => {
    void loadHomeSignals();
    return () => {
      unregisterContract("revenue_intelligence.persistent_signals");
    };
  }, [loadHomeSignals, unregisterContract]);

  async function handleHomeSignalAction(
    signal: RevenueIntelligencePersistentSignal,
    action: "mark_seen" | "snooze",
  ) {
    if (pendingSignalActionId) return;
    setPendingSignalActionId(signal.id);
    try {
      const snoozedUntil = action === "snooze"
        ? new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
        : null;
      await transitionPersistentRevenueIntelligenceSignal({
        action,
        signalId: signal.id,
        snoozedUntil,
      });
      await loadHomeSignals();
      window.dispatchEvent(new Event(REVENUE_INTELLIGENCE_SIGNALS_CHANGED_EVENT));
      toast({
        title: action === "snooze" ? "Signal snoozed for 24 hours" : "Signal marked as seen",
      });
    } catch {
      toast({
        title: "Signal could not be updated",
        description: "Try again. No Signal state was changed.",
        variant: "destructive",
      });
    } finally {
      setPendingSignalActionId(null);
    }
  }

  useEffect(() => {
    setInput("");
    setLastContext(null);
    setLastSemanticPlan(null);
    setActiveRecordResult(null);
    setMessages([]);
    setAvailabilityState(null);
    setConversationId(crypto.randomUUID());
    setSelectedSavedAnalysisIds([]);
    autoExpandedChartMessageIdsRef.current.clear();
    activeResultContractsRef.current = [];
    activeScopeActionIdsRef.current = [];
  }, [actorAccessKey]);

  useEffect(() => {
    let active = true;
    if (!currentUser?.company_id || !["admin", "super_admin"].includes(currentUser.role)) {
      setSavedAnalyses([]);
      return;
    }
    listRevenueIntelligenceSavedAnalyses(currentUser.company_id)
      .then((rows) => {
        if (!active) return;
        setSavedAnalyses(rows);
        setSavedAnalysesAvailable(true);
      })
      .catch(() => {
        if (!active) return;
        setSavedAnalyses([]);
        setSavedAnalysesAvailable(false);
      });
    return () => {
      active = false;
    };
  }, [currentUser?.company_id, currentUser?.role]);

  useEffect(() => () => {
    if (activityTimerRef.current) window.clearTimeout(activityTimerRef.current);
  }, []);

  useEffect(() => {
    const handleOffline = () => setAvailabilityState("offline");
    const handleOnline = () => setAvailabilityState(current => (
      current === "offline" ? "simplified" : current
    ));
    if (!navigator.onLine) handleOffline();
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  useEffect(() => {
    contractsRef.current = contracts;
    factsRef.current = facts;
  }, [contracts, facts]);

  useEffect(() => {
    if (previousLocationRef.current === location) return;
    previousLocationRef.current = location;
    if (activeRecordResult) return;
    activeResultContractsRef.current = [];
    activeScopeActionIdsRef.current = [];
    setLastContext(null);
    setLastSemanticPlan(null);
  }, [activeRecordResult, location]);

  function captureUnsupportedQuestion(question: string) {
    const isProduction = import.meta.env.VITE_SITE_URL === "https://dashboard.revelate.app";
    if (!isProduction) return;

    posthog?.capture("revenue_intelligence_chat_unsupported_question", {
      question,
      route: window.location.pathname,
      available_insight_ids: insights.map(insight => insight.insightId),
      has_facts: Boolean(facts),
    });
  }

  async function submitQuestion(
    question: string,
    contextOverride: RevenueIntelligenceChatTurnContext | null = lastContext,
  ) {
    const trimmedQuestion = question.trim();
    if (!trimmedQuestion || working) return;
    const swedish = isRevenueIntelligenceSwedish(trimmedQuestion);
    const submittedUserMessage: ChatMessage = {
      id: createMessageId(),
      role: "user",
      content: trimmedQuestion,
    };
    const conversation = messages.map(message => ({
      role: message.role,
      content: message.content,
    }));
    setMessages(previous => [
      ...previous,
      submittedUserMessage,
    ]);
    setInput("");
    setWorking(true);
    setActivityLabel(null);
    activityTimerRef.current = window.setTimeout(() => {
      setActivityLabel(getChatActivityLabel("understanding", swedish));
    }, 450);

    try {
      let plannedMessage: ChatMessage | null = null;
      let composeApprovedAnswer = false;
      let semanticPlannerCompleted = false;
      let activeAccessToken: string | null = null;
      let resolvedScopeLabels: string[] = [];
      let resolvedScopeActionIds = activeScopeActionIdsRef.current;
      const activeContractIds = new Set(
        activeResultContractsRef.current.map(contract => contract.contractId),
      );
      let planningContracts = activeResultContractsRef.current.length
        ? [
            ...activeResultContractsRef.current,
            ...contractsRef.current.filter(contract => !activeContractIds.has(contract.contractId)),
          ]
        : contractsRef.current;
      const catalogueResolution = await resolveContractsForQuestion(
        trimmedQuestion,
        planningContracts.map(contract => contract.contractId),
      );
      if (catalogueResolution.status === "resolved") {
        setActivityLabel(getChatActivityLabel("finding", swedish));
        const resolvedContractIds = new Set(
          catalogueResolution.contracts.map(contract => contract.contractId),
        );
        planningContracts = [
          ...catalogueResolution.contracts,
          ...contractsRef.current.filter(
            contract => !resolvedContractIds.has(contract.contractId),
          ),
        ];
        activeResultContractsRef.current = catalogueResolution.contracts;
        resolvedScopeLabels = catalogueResolution.contracts
          .map(contract => [
            contract.scope.pageLabel,
            contract.scope.periodLabel,
            contract.scope.filtersLabel,
          ].filter(Boolean).join(" · "))
          .filter(Boolean);
      } else if (
        catalogueResolution.status === "unavailable"
        || catalogueResolution.status === "failed"
      ) {
        plannedMessage = {
          id: createMessageId(),
          role: "assistant",
          content: catalogueResolution.reason,
          followUpQuestions: [],
        };
      }
      const activeChart = [...messages].reverse()
        .find(message => message.chart)?.chart;
      const chartRequested = isRevenueIntelligenceChartRequest(trimmedQuestion);
      if (isSaveAnalysisQuestion(trimmedQuestion)) {
        setActivityLabel(getChatActivityLabel("saving", swedish));
        if (activeChart) {
          const saved = await saveChartAnalysis(activeChart);
          plannedMessage = {
            id: createMessageId(),
            role: "assistant",
            content: saved
              ? `${saved.name} is saved and ready to refresh with current data.`
              : "I couldn't save this analysis right now.",
            followUpQuestions: saved
              ? ["What is the source?", "Show another view"]
              : ["Save this analysis"],
          };
        } else {
          plannedMessage = {
            id: createMessageId(),
            role: "assistant",
            content: "Create or open a chart first, then I can save it.",
            followUpQuestions: ["Show me a chart"],
          };
        }
      } else if (isSourceQuestion(trimmedQuestion) && activeChart) {
        setActivityLabel(getChatActivityLabel("preparing", swedish));
        const fieldLabels = [
          ...activeChart.dataset.dimensions.map(field => field.label),
          ...activeChart.dataset.measures.map(field => field.label),
        ];
        plannedMessage = {
          id: createMessageId(),
          role: "assistant",
          content: [
            `Source: ${activeChart.dataset.label}`,
            activeChart.period ? `Scope: ${activeChart.period}` : null,
            `Fields: ${fieldLabels.join(", ")}`,
            ...(activeChart.dataset.caveats?.length
              ? ["", `Limitation: ${activeChart.dataset.caveats.join(" ")}`]
              : []),
          ].filter(line => line !== null).join("\n"),
          followUpQuestions: ["Save this analysis"],
        };
      } else if (
        !plannedMessage
        &&
        planningContracts.length
        && currentUser?.company_id
        && ["admin", "super_admin"].includes(currentUser.role)
      ) {
        const requestedScopeActions = getRevenueIntelligenceRequestedScopeActions(
          trimmedQuestion,
          scopeActions,
        );
        if (requestedScopeActions.length) {
          setActivityLabel(getChatActivityLabel("finding", swedish));
          const resolver = requestedScopeActions.find(action => action.resolve)?.resolve;
          if (resolver) {
            const resolvedContracts = await resolver(requestedScopeActions);
            if (resolvedContracts.length) {
              planningContracts = resolvedContracts;
              activeResultContractsRef.current = resolvedContracts;
              resolvedScopeLabels = requestedScopeActions.map(action => action.label);
              resolvedScopeActionIds = requestedScopeActions.map(action => action.actionId);
              activeScopeActionIdsRef.current = resolvedScopeActionIds;
            } else {
              plannedMessage = {
                id: createMessageId(),
                role: "assistant",
                content: "I couldn't resolve that scope reliably.",
                followUpQuestions: [],
              };
            }
          } else {
            plannedMessage = {
              id: createMessageId(),
              role: "assistant",
              content: "I can't analyse that scope independently from this view yet.",
              followUpQuestions: [],
            };
          }
        }
        if (!plannedMessage) {
          setActivityLabel(getChatActivityLabel("finding", swedish));
          try {
            activeAccessToken = await getRevenueIntelligenceChatAccessToken(
              () => supabaseClient.auth.getSession(),
            );
          } catch (error) {
            setAvailabilityState(getRevenueIntelligenceChatAvailabilityState(
              error,
              navigator.onLine,
            ));
          }
          if (activeAccessToken) {
          const plannerStartedAt = performance.now();
          try {
            const currentContracts = planningContracts;
            const currentDatasets = currentContracts.flatMap(contract => contract.datasets);
            const currentInsights = currentContracts.flatMap(contract => contract.insights);
            const currentMetrics = currentContracts.flatMap(contract => contract.metrics);
            const currentRecordSets = currentContracts.flatMap(contract => contract.recordSets);
            const planned = await runRevenueIntelligenceChatRequest(
              signal => requestRevenueIntelligenceSemanticPlan({
                accessToken: activeAccessToken!,
                companyId: String(currentUser.company_id),
                contracts: currentContracts,
                conversation,
                conversationId,
                previousPlan: lastSemanticPlan
                  ? toPlannerPreviousPlan(lastSemanticPlan)
                  : null,
                activeResult: activeRecordResult,
                question: trimmedQuestion,
                signal,
              }),
              REVENUE_INTELLIGENCE_PLANNER_TIMEOUT_MS,
            );
            const plan = planned.plan;
            semanticPlannerCompleted = true;
            posthog?.capture("revenue_intelligence_chat_planner_completed", {
              contract_id: plan.contractId,
              duration_ms: Math.round(performance.now() - plannerStartedAt),
              intent: plan.intent,
              model: planned.usage.model,
              operation: plan.operation,
              output_tokens: planned.usage.outputTokens,
              input_tokens: planned.usage.inputTokens,
              route: window.location.pathname,
            });
            setAvailabilityState(null);
            setLastSemanticPlan(plan);
            if (
              (plan.intent === "chart" || plan.intent === "modify_chart")
              && plan.chart
              && (plan.datasetId || (plan.recordSetId && plan.recordQuery))
            ) {
              setActivityLabel(getChatActivityLabel("drawing", swedish));
              const selectedRecordSet = plan.recordSetId
                ? currentRecordSets.find(item => item.recordSetId === plan.recordSetId)
                : null;
              const resolvedDataset = plan.datasetId
                ? currentDatasets.find(item => item.datasetId === plan.datasetId)
                : selectedRecordSet && plan.recordQuery
                  ? buildRevenueIntelligenceRecordChartDataset({
                      activeRecordIds: activeRecordResult?.recordSetId === plan.recordSetId
                        ? activeRecordResult.recordIds
                        : [],
                      parentRecordIds: activeRecordResult?.recordSetId === plan.recordSetId
                        ? activeRecordResult.parentRecordIds ?? []
                        : [],
                      chart: plan.chart,
                      query: plan.recordQuery,
                      recordSet: selectedRecordSet,
                    })
                  : null;
              if (resolvedDataset) {
                const selectedContract = currentContracts.find(
                  contract => contract.contractId === plan.contractId,
                );
                const filteredDataset = plan.datasetId
                  ? applyPlanFilters(resolvedDataset, plan)
                  : resolvedDataset;
                const selectedInsight = plan.insightId
                  ? currentInsights.find(item => item.insightId === plan.insightId)
                  : null;
                const recordIds = filteredDataset.rows.flatMap(row => (
                  typeof row.__record_id === "string" ? [row.__record_id] : []
                ));
                plannedMessage = {
                  id: createMessageId(),
                  role: "assistant",
                  content: selectedInsight?.title ?? filteredDataset.description,
                  followUpQuestions: [
                    "Show another view",
                    "What is the source?",
                    "Save this analysis",
                  ],
                  chart: {
                    conclusion: selectedInsight?.title ?? null,
                    dataset: filteredDataset,
                    instruction: trimmedQuestion,
                    period: selectedContract?.scope
                      ? [
                          selectedContract.scope.periodLabel,
                          selectedContract.scope.filtersLabel,
                        ].filter(Boolean).join(" · ") || null
                      : null,
                    plan,
                    recordIds,
                    scopeActionIds: resolvedScopeActionIds,
                  },
                };
                if (selectedRecordSet && plan.recordQuery) {
                  if (recordIds.length) {
                    setActiveRecordResult({
                      contractId: plan.contractId ?? "",
                      recordSetId: selectedRecordSet.recordSetId,
                      recordIds,
                      parentRecordIds: getRevenueIntelligenceParentRecordIds({
                        activeResult: activeRecordResult,
                        nextRecordIds: recordIds,
                        preserveExistingParent: doesRevenueIntelligenceQuerySelectNamedRecords(
                          selectedRecordSet,
                          plan.recordQuery,
                        ),
                        scope: plan.recordQuery.scope,
                      }),
                      selectedFields: plan.recordQuery.selectFields,
                      sourceQuery: plan.recordQuery.scope === "record_set"
                        ? plan.recordQuery
                        : activeRecordResult?.sourceQuery ?? plan.recordQuery,
                    });
                    activeResultContractsRef.current = planningContracts;
                  }
                }
              }
            } else if (!chartRequested) {
              setActivityLabel(getChatActivityLabel("preparing", swedish));
              const semanticResponse = answerRevenueIntelligenceSemanticPlan(
                plan,
                currentInsights,
                currentMetrics,
                currentRecordSets,
                activeRecordResult?.recordSetId === plan.recordSetId
                  ? activeRecordResult.recordIds
                  : [],
                activeRecordResult?.recordSetId === plan.recordSetId
                  ? activeRecordResult.parentRecordIds ?? []
                  : [],
              );
              const response = semanticResponse;
              if (response) {
                plannedMessage = {
                  id: createMessageId(),
                  role: "assistant",
                  content: response.answer,
                  followUpQuestions: response.followUpQuestions,
                };
                setLastContext(response.context);
                if (
                  response.context.contractId
                  && response.context.recordSetId
                  && response.context.recordIds?.length
                  && plan.recordQuery
                ) {
                  setActiveRecordResult({
                    contractId: response.context.contractId,
                    recordSetId: response.context.recordSetId,
                    recordIds: response.context.recordIds,
                    parentRecordIds: getRevenueIntelligenceParentRecordIds({
                      activeResult: activeRecordResult,
                      nextRecordIds: response.context.recordIds,
                      preserveExistingParent: doesRevenueIntelligenceQuerySelectNamedRecords(
                        currentRecordSets.find(recordSet => (
                          recordSet.recordSetId === response.context.recordSetId
                        ))!,
                        plan.recordQuery,
                      ),
                      scope: plan.recordQuery.scope,
                    }),
                    selectedFields: response.context.selectedFields ?? [],
                    sourceQuery: plan.recordQuery?.scope === "record_set"
                      ? plan.recordQuery
                      : activeRecordResult?.sourceQuery ?? plan.recordQuery,
                  });
                  activeResultContractsRef.current = planningContracts;
                } else if (
                  plan.recordSetId === null
                  && !["access_denied", "clarification", "unsupported"].includes(response.intentId)
                ) {
                  setActiveRecordResult(null);
                  activeResultContractsRef.current = [];
                }
                composeApprovedAnswer = ![
                  "access_denied",
                  "clarification",
                  "unsupported",
                ].includes(response.intentId);
              }
            }
          } catch (error) {
            posthog?.capture("revenue_intelligence_chat_planner_fallback", {
              ...getRequestErrorProperties(error),
              duration_ms: Math.round(performance.now() - plannerStartedAt),
              route: window.location.pathname,
            });
            setAvailabilityState(getRevenueIntelligenceChatAvailabilityState(
              error,
              navigator.onLine,
            ));
            // The deterministic path is the safe local and availability fallback.
          }
          }
        }
      }

      if (!plannedMessage && chartRequested) {
        if (
          activeRecordResult
          && referencesRevenueIntelligenceActiveResult(trimmedQuestion)
        ) {
          plannedMessage = {
            id: createMessageId(),
            role: "assistant",
            content: "I couldn’t draw the requested chart from the active records reliably.",
            followUpQuestions: [],
          };
        }
      }

      if (!plannedMessage && chartRequested) {
        const deterministicChart = buildRevenueIntelligenceDeterministicChartPlan(
          trimmedQuestion,
          planningContracts,
        );
        if (deterministicChart?.plan.chart && deterministicChart.plan.datasetId) {
          const dataset = planningContracts.flatMap(contract => contract.datasets).find(
            item => item.datasetId === deterministicChart.plan.datasetId,
          );
          if (dataset) {
            setActivityLabel(getChatActivityLabel("drawing", swedish));
            setLastSemanticPlan(deterministicChart.plan);
            plannedMessage = {
              id: createMessageId(),
              role: "assistant",
              content: [
                dataset.label,
                deterministicChart.periodLabel,
              ].filter(Boolean).join(" · "),
              followUpQuestions: [
                "What is the source?",
                "Save this analysis",
              ],
              chart: {
                conclusion: null,
                dataset,
                instruction: trimmedQuestion,
                period: deterministicChart.periodLabel,
                plan: deterministicChart.plan,
                recordIds: [],
                scopeActionIds: resolvedScopeActionIds,
              },
            };
          }
        }
        if (!plannedMessage) {
          const limitation = getRevenueIntelligenceChartLimitation(
            trimmedQuestion,
            planningContracts,
          );
          if (limitation) {
            setActivityLabel(getChatActivityLabel("preparing", swedish));
            plannedMessage = {
              id: createMessageId(),
              role: "assistant",
              content: limitation.answer,
              followUpQuestions: limitation.followUpQuestions,
            };
          }
        }
      }

      if (!plannedMessage) {
        const activeSignalDetail = !semanticPlannerCompleted && activeRecordResult
          ? answerRevenueIntelligenceActiveSignalDetailFollowUp(
              trimmedQuestion,
              planningContracts.flatMap(contract => contract.recordSets).find(
                recordSet => recordSet.recordSetId === activeRecordResult.recordSetId,
              ),
              activeRecordResult.recordIds,
            )
          : null;
        if (activeSignalDetail) {
          setLastContext(activeSignalDetail.context);
          plannedMessage = {
            id: createMessageId(),
            role: "assistant",
            content: activeSignalDetail.answer,
            followUpQuestions: activeSignalDetail.followUpQuestions,
          };
          composeApprovedAnswer = true;
        }
      }

      if (!plannedMessage && !semanticPlannerCompleted) {
        const activeSignalFollowUp = activeRecordResult
          ? answerRevenueIntelligenceActiveSignalPriorityFollowUp(
              trimmedQuestion,
              planningContracts.flatMap(contract => contract.recordSets).find(
                recordSet => recordSet.recordSetId === activeRecordResult.recordSetId,
              ),
              activeRecordResult.recordIds,
            )
          : null;
        if (activeSignalFollowUp) {
          setLastContext(activeSignalFollowUp.context);
          plannedMessage = {
            id: createMessageId(),
            role: "assistant",
            content: activeSignalFollowUp.answer,
            followUpQuestions: activeSignalFollowUp.followUpQuestions,
          };
          composeApprovedAnswer = true;
        }
      }

      if (!plannedMessage) {
        const response = answerRevenueIntelligenceQuestion(
          trimmedQuestion,
          planningContracts.flatMap(contract => contract.insights),
          planningContracts.find(contract => contract.facts)?.facts ?? factsRef.current,
          contextOverride,
          planningContracts.flatMap(contract => contract.metrics),
          accessNotice,
          planningContracts.flatMap(contract => contract.recordSets),
        );
        if (isUnsupportedRevenueIntelligenceChatResponse(response)) {
          captureUnsupportedQuestion(trimmedQuestion);
        }
        setLastContext(response.context);
        plannedMessage = {
          id: createMessageId(),
          role: "assistant",
          content: response.answer,
          followUpQuestions: response.followUpQuestions,
        };
        composeApprovedAnswer = ![
          "access_denied",
          "clarification",
          "unsupported",
        ].includes(response.intentId);
      }

      if (
        plannedMessage
        && composeApprovedAnswer
        && !plannedMessage.chart
        && !plannedMessage.brief
        && currentUser?.company_id
        && ["admin", "super_admin"].includes(currentUser.role)
      ) {
        if (!activeAccessToken) {
          try {
            activeAccessToken = await getRevenueIntelligenceChatAccessToken(
              () => supabaseClient.auth.getSession(),
            );
          } catch (error) {
            setAvailabilityState(getRevenueIntelligenceChatAvailabilityState(
              error,
              navigator.onLine,
            ));
          }
        }
        if (activeAccessToken) {
          setActivityLabel(getChatActivityLabel("preparing", swedish));
          const composerStartedAt = performance.now();
          try {
            const composed = await runRevenueIntelligenceChatRequest(
              signal => requestRevenueIntelligenceAnswerComposition({
                accessToken: activeAccessToken!,
                approvedAnswer: plannedMessage!.content,
                companyId: String(currentUser.company_id),
                conversationId,
                question: trimmedQuestion,
                signal,
              }),
              REVENUE_INTELLIGENCE_COMPOSER_TIMEOUT_MS,
            );
            plannedMessage = {
              ...plannedMessage,
              content: composed.answer,
            };
            posthog?.capture("revenue_intelligence_chat_composer_completed", {
              duration_ms: Math.round(performance.now() - composerStartedAt),
              input_tokens: composed.usage.inputTokens,
              model: composed.usage.model,
              output_tokens: composed.usage.outputTokens,
              route: window.location.pathname,
            });
            setAvailabilityState(null);
          } catch (error) {
            posthog?.capture("revenue_intelligence_chat_composer_fallback", {
              ...getRequestErrorProperties(error),
              duration_ms: Math.round(performance.now() - composerStartedAt),
              route: window.location.pathname,
            });
            setAvailabilityState(getRevenueIntelligenceChatAvailabilityState(
              error,
              navigator.onLine,
            ));
            // Keep the approved deterministic answer if composition is unavailable.
          }
        }
      }

      if (
        plannedMessage
        && resolvedScopeLabels.length
        && !plannedMessage.chart
        && !plannedMessage.brief
      ) {
        plannedMessage = {
          ...plannedMessage,
          content: `${plannedMessage.content}\n\nScope: ${resolvedScopeLabels.join(" · ")}`,
        };
      }

      if (plannedMessage && !plannedMessage.chart && !plannedMessage.brief) {
        plannedMessage = {
          ...plannedMessage,
          content: localizeRevenueIntelligenceApprovedFallback(
            plannedMessage.content,
            trimmedQuestion,
          ),
        };
      }

      if (plannedMessage.followUpQuestions?.length) {
        const languageSample = [
          ...messages.filter(message => message.role === "user").map(message => message.content),
          trimmedQuestion,
        ].join("\n");
        plannedMessage = {
          ...plannedMessage,
          followUpQuestions: localizeRevenueIntelligenceFollowUps(
            languageSample,
            plannedMessage.followUpQuestions,
          ),
        };
      }

      setMessages(previous => [
        ...(previous.some(message => message.id === submittedUserMessage.id)
          ? previous
          : [...previous, submittedUserMessage]),
        plannedMessage,
      ]);
    } finally {
      if (activityTimerRef.current) {
        window.clearTimeout(activityTimerRef.current);
        activityTimerRef.current = null;
      }
      setActivityLabel(null);
      setWorking(false);
    }
  }

  async function saveChartAnalysis(chart: NonNullable<ChatMessage["chart"]>) {
    if (!currentUser?.id || !currentUser.company_id) return null;
    try {
      const saved = await saveRevenueIntelligenceAnalysis({
        companyId: currentUser.company_id,
        conclusion: chart.conclusion,
        dataset: chart.dataset,
        instruction: chart.instruction,
        name: chart.plan.chart?.title ?? chart.dataset.label,
        periodBehavior: "relative",
        periodLabel: chart.period,
        plan: chart.plan,
        route: `${window.location.pathname}${window.location.search}`,
        userId: currentUser.id,
        recordIds: chart.recordIds,
        scopeActionIds: chart.scopeActionIds,
      });
      setSavedAnalyses(previous => [
        saved,
        ...previous.filter(item => item.id !== saved.id),
      ]);
      toast({
        title: "Saved to Agents",
        description: "The analysis is ready to open or refresh.",
        action: (
          <ToastAction
            altText="Open Agents"
            onClick={() => setLocation(buildRevenueIntelligenceContextHref(
              REVENUE_INTELLIGENCE_AGENTS_PATH,
              window.location.href,
            ))}
          >
            View
          </ToastAction>
        ),
      });
      return saved;
    } catch {
      toast({
        title: "Saving is not available yet",
        description: "Try again later.",
        variant: "destructive",
      });
      return null;
    }
  }

  async function openSavedAnalysis(savedAnalysis: SavedAnalysisRow, refresh = false) {
    const recipe = parseSavedAnalysisRecipe(savedAnalysis.recipe);
    if (!recipe || !currentUser?.id || !currentUser.company_id) return;
    if (!recipe.plan.chart) return;
    let result: ReturnType<typeof parseAnalysisRunResult> = null;
    let savedRecordIds = recipe.recordIds;
    let resolvedContracts = activeResultContractsRef.current.length
      ? activeResultContractsRef.current
      : contracts;
    if (!refresh) {
      if (!savedAnalysis.latest_run_id) {
        toast({
          title: "No saved run yet",
          description: "Refresh the analysis to create its first result.",
        });
        return;
      }
      try {
        const run = await getRevenueIntelligenceAnalysisRun({
          companyId: currentUser.company_id,
          runId: savedAnalysis.latest_run_id,
        });
        result = parseAnalysisRunResult(run.result);
      } catch {
        result = null;
      }
    } else {
      if (!savedRecordIds.length && savedAnalysis.latest_run_id) {
        try {
          const previousRun = await getRevenueIntelligenceAnalysisRun({
            companyId: currentUser.company_id,
            runId: savedAnalysis.latest_run_id,
          });
          const previousResult = parseAnalysisRunResult(previousRun.result);
          savedRecordIds = previousResult?.dataset.rows.flatMap(row => (
            typeof row.__record_id === "string" ? [row.__record_id] : []
          )) ?? [];
        } catch {
          savedRecordIds = [];
        }
      }
      const savedScopeActions = recipe.scopeActionIds.flatMap(actionId => {
        const action = scopeActions.find(item => item.actionId === actionId);
        return action ? [action] : [];
      });
      const periodToken = recipe.periodLabel?.split(" · ")[0]?.trim().toLocaleLowerCase();
      const legacyPeriodAction = scopeActions.find(action => (
        action.field === "period"
        && (
          action.values?.some(value => value.toLocaleLowerCase() === periodToken)
          || action.label.toLocaleLowerCase() === periodToken
        )
      ));
      const refreshScopeActions = savedScopeActions.length
        ? savedScopeActions
        : legacyPeriodAction
          ? [legacyPeriodAction]
          : [];
      const resolver = refreshScopeActions.find(action => action.resolve)?.resolve;
      if (resolver) {
        resolvedContracts = await resolver(refreshScopeActions);
      }
      const contract = resolvedContracts.find(item => item.contractId === recipe.contractId);
      if (contract && recipe.plan.datasetId) {
        const dataset = contract.datasets.find(item => item.datasetId === recipe.plan.datasetId);
        if (dataset) result = {
          dataset: applyPlanFilters(dataset, recipe.plan),
          plan: recipe.plan,
        };
      } else if (
        contract
        && recipe.plan.recordSetId
        && recipe.plan.recordQuery
      ) {
        const recordSet = contract.recordSets.find(
          item => item.recordSetId === recipe.plan.recordSetId,
        );
        if (recordSet) {
          const query = savedRecordIds.length
            ? {
                ...recipe.plan.recordQuery,
                filters: [],
                limit: Math.max(1, savedRecordIds.length),
                minimumGroupSize: 1,
                offset: 0,
                orderBy: null,
                scope: "active_result" as const,
              }
            : recipe.plan.recordQuery;
          const dataset = buildRevenueIntelligenceRecordChartDataset({
            activeRecordIds: savedRecordIds,
            chart: recipe.plan.chart,
            query,
            recordSet,
          });
          if (dataset) {
            const resolvedIds = dataset.rows.flatMap(row => (
              typeof row.__record_id === "string" ? [row.__record_id] : []
            ));
            const exactSelection = !savedRecordIds.length
              || (
                resolvedIds.length === savedRecordIds.length
                && savedRecordIds.every(id => resolvedIds.includes(id))
              );
            if (exactSelection) result = { dataset, plan: recipe.plan };
          }
        }
      }
    }
    if (!result) {
      toast({
        title: refresh ? "Analysis could not be refreshed" : "Saved result is unavailable",
        description: refresh
          ? "The saved scope could not be resolved safely."
          : "The latest successful run could not be loaded.",
        variant: "destructive",
      });
      return;
    }
    if (refresh) {
      try {
        const run = await recordRevenueIntelligenceAnalysisRefresh({
          companyId: currentUser.company_id,
          dataset: result.dataset,
          recipe,
          savedAnalysisId: savedAnalysis.id,
          userId: currentUser.id,
        });
        setSavedAnalyses(previous => previous.map(item => (
          item.id === savedAnalysis.id
            ? { ...item, latest_run_id: run.id, updated_at: new Date().toISOString() }
            : item
        )));
      } catch {
        toast({
          title: "Refresh could not be recorded",
          description: "The chart uses current data, but the run was not saved.",
          variant: "destructive",
        });
      }
    }
    setLastSemanticPlan(result.plan);
    activeResultContractsRef.current = resolvedContracts;
    setMessages(previous => [
      ...previous,
      {
        id: createMessageId(),
        role: "assistant",
        content: refresh
          ? `${savedAnalysis.name} refreshed with current data.`
          : savedAnalysis.original_instruction,
        followUpQuestions: ["Change the chart", "What is the source?"],
        chart: {
          conclusion: recipe.conclusion,
          dataset: result.dataset,
          instruction: savedAnalysis.original_instruction,
          period: recipe.periodLabel,
          plan: result.plan,
          recordIds: result.dataset.rows.flatMap(row => (
            typeof row.__record_id === "string" ? [row.__record_id] : []
          )),
          scopeActionIds: recipe.scopeActionIds,
        },
      },
    ]);
    if (agentsMode) setLocation(REVENUE_INTELLIGENCE_HOME_PATH);
  }

  async function createBriefFromSelection() {
    if (
      !currentUser?.id
      || !currentUser.company_id
      || !selectedSavedAnalysisIds.length
    ) return;
    const selectedRows = (await Promise.all(selectedSavedAnalysisIds.map(async (savedAnalysisId) => {
      const savedAnalysis = savedAnalyses.find(item => item.id === savedAnalysisId);
      if (!savedAnalysis?.latest_run_id) return null;
      const recipe = parseSavedAnalysisRecipe(savedAnalysis.recipe);
      if (!recipe?.plan.chart) return null;
      try {
        const run = await getRevenueIntelligenceAnalysisRun({
          companyId: currentUser.company_id,
          runId: savedAnalysis.latest_run_id,
        });
        const result = parseAnalysisRunResult(run.result);
        if (!result) return null;
        return {
        conclusion: recipe.conclusion,
        dataset: result.dataset,
        plan: result.plan,
        recipe,
        savedAnalysisId,
        };
      } catch {
        return null;
      }
    }))).flatMap(row => row ? [row] : []).slice(0, 3);
    if (!selectedRows.length) {
      toast({
        title: "Refresh the selected analyses",
        description: "A current saved run is required before creating a Brief.",
      });
      return;
    }
    try {
      const title = `Revenue Brief · ${new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date())}`;
      const brief = await createRevenueIntelligenceBrief({
        companyId: currentUser.company_id,
        name: title,
        savedAnalysisIds: selectedRows.map(row => row.savedAnalysisId),
        userId: currentUser.id,
      });
      setSelectedSavedAnalysisIds([]);
      setMessages(previous => [
        ...previous,
        {
          id: createMessageId(),
          role: "assistant",
          content: `${brief.name} is ready.`,
          brief: {
            briefId: brief.id,
            items: selectedRows.map(({ conclusion, dataset, plan }) => ({
              conclusion,
              dataset,
              plan,
            })),
            period: selectedRows[0]?.recipe.periodLabel ?? null,
            title,
          },
        },
      ]);
      if (agentsMode) setLocation(REVENUE_INTELLIGENCE_HOME_PATH);
    } catch {
      toast({
        title: "Brief could not be created",
        description: "Select up to three available Saved analyses and try again.",
        variant: "destructive",
      });
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void submitQuestion(input);
  }

  function renderHomeSignalsSection() {
    return (
      <section aria-labelledby="home-signals-heading">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 id="home-signals-heading" className="text-sm font-semibold text-gray-900">
            Needs attention
          </h2>
          {homeSignals.length > 2 && (
            <button
              type="button"
              className="text-xs text-[#5E548E]"
              onClick={() => void submitQuestion("Show all active Signals")}
            >
              All Signals
            </button>
          )}
        </div>

        {homeSignalsLoading ? (
          <div className="flex h-28 items-center justify-center rounded-xl border border-gray-200 bg-white">
            <span className="flex items-center gap-1" aria-label="Loading Signals">
              {[0, 1, 2].map(index => (
                <span
                  key={index}
                  className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#9F86C0]"
                  style={{ animationDelay: `${index * 140}ms` }}
                />
              ))}
            </span>
          </div>
        ) : !canReadCompanySignals ? (
          <div className="rounded-xl border border-gray-200 bg-white px-4 py-5 text-sm text-gray-600">
            Company Signals are not available for your role yet.
          </div>
        ) : homeSignalsError ? (
          <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white px-4 py-5">
            <p className="text-sm text-gray-600">Signals could not be loaded.</p>
            <Button type="button" variant="outline" size="sm" onClick={() => void loadHomeSignals()}>
              Retry
            </Button>
          </div>
        ) : homeSignals.length === 0 ? (
          <div className="rounded-xl border border-gray-200 bg-white px-4 py-5 text-sm text-gray-600">
            No new or active Signals.
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {homeSignals.slice(0, 2).map(signal => {
              const availableActions = Array.isArray(signal.available_actions)
                ? signal.available_actions.filter((action): action is string => typeof action === "string")
                : [];
              const evidenceExpanded = expandedEvidenceSignalIds.has(signal.id);
              const actionPending = pendingSignalActionId === signal.id;
              const signalKind = getPersistentSignalKindLabel(signal.event_type);
              return (
                <article key={signal.id} className="rounded-xl border border-[#5E548E]/15 bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 text-[10px] font-medium uppercase tracking-wide">
                        <span className={cn(
                          signal.event_type === "risk_signal" && "text-[#9F3A4A]",
                          signal.event_type === "buying_signal" && "text-[#1D819A]",
                          signal.event_type === "analysis_signal" && "text-[#5E548E]",
                        )}>
                          {signalKind}
                        </span>
                        {signal.lifecycle_state === "new" && (
                          <span className="rounded-full bg-[#9F86C0]/10 px-2 py-0.5 text-[#5E548E]">New</span>
                        )}
                      </div>
                      <h3 className="mt-2 text-sm font-semibold leading-5 text-gray-900">
                        {signal.title}
                      </h3>
                      {signal.deal_id ? (
                        <button
                          type="button"
                          className="mt-1 max-w-full truncate text-left text-xs text-[#5E548E] underline-offset-2 hover:underline"
                          onClick={() => setLocation(`/revenue-intelligence/deal/${signal.deal_id}?dealDetailsViewSource=revenue-intelligence-home&signalId=${signal.id}`)}
                        >
                          {signal.deal?.name ?? "Deal not specified"}
                        </button>
                      ) : (
                        <p className="mt-1 text-xs text-gray-600">
                          {signal.deal?.name ?? "Deal not specified"}
                        </p>
                      )}
                    </div>
                    <span className="flex shrink-0 items-center gap-1 text-[10px] text-gray-500">
                      <Clock3 className="h-3 w-3" />
                      {formatSignalTimestamp(signal.detected_at)}
                    </span>
                  </div>

                  <p className="mt-3 text-xs text-gray-600">
                    Signal strength {formatPersistentSignalPercentage(signal.strength)} · Confidence {formatPersistentSignalPercentage(signal.confidence)}
                  </p>

                  <blockquote className="mt-3 border-l-2 border-[#9F86C0]/60 pl-3 text-xs leading-5 text-gray-700">
                    “{signal.evidence}”
                  </blockquote>
                  {evidenceExpanded && (
                    <div className="mt-2 rounded-lg bg-gray-50 px-3 py-2 text-[10px] leading-4 text-gray-500">
                      Source: {signal.category.replace(/_/gu, " ")} · Detected {formatSignalTimestamp(signal.detected_at)}
                    </div>
                  )}

                  <TooltipProvider>
                    <div className="mt-4 flex items-center gap-1.5">
                      {availableActions.includes("view_evidence") && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-sm"
                              aria-label={evidenceExpanded ? "Hide source" : "View source"}
                              title={evidenceExpanded ? "Hide source" : "View source"}
                              onClick={() => setExpandedEvidenceSignalIds(current => {
                                const next = new Set(current);
                                if (next.has(signal.id)) next.delete(signal.id);
                                else next.add(signal.id);
                                return next;
                              })}
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {evidenceExpanded ? "Hide source" : "View source"}
                          </TooltipContent>
                        </Tooltip>
                      )}
                      {availableActions.includes("mark_seen") && signal.lifecycle_state === "new" && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-sm"
                              aria-label="Mark as seen"
                              title="Mark as seen"
                              disabled={actionPending}
                              onClick={() => void handleHomeSignalAction(signal, "mark_seen")}
                            >
                              <Check className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Mark as seen</TooltipContent>
                        </Tooltip>
                      )}
                      {availableActions.includes("snooze") && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-sm"
                              aria-label="Snooze 24h"
                              title="Snooze 24h"
                              disabled={actionPending}
                              onClick={() => void handleHomeSignalAction(signal, "snooze")}
                            >
                              <Clock3 className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Snooze 24h</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </TooltipProvider>
                </article>
              );
            })}
          </div>
        )}
      </section>
    );
  }

  if (agentsMode) {
    return (
      <div className="col-start-1 row-start-1 z-10 min-h-screen min-w-0 overflow-y-auto bg-gray-50 pt-[60px] md:col-start-2">
        <div className="mx-auto w-full max-w-5xl px-6 pb-12 pt-8">
          <header>
            <h1 className="text-2xl font-semibold text-[#231942]">Agents</h1>
            <p className="mt-1 text-sm text-gray-500">
              Saved work and reusable agent workflows.
            </p>
          </header>

          <section aria-labelledby="saved-analyses-heading" className="mt-8">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 id="saved-analyses-heading" className="text-lg font-semibold text-[#231942]">
                  Saved analyses
                </h2>
                <p className="mt-1 text-xs text-gray-500">
                  Open a saved result, refresh it with current data or combine up to three analyses in a Brief.
                </p>
              </div>
              {selectedSavedAnalysisIds.length > 0 && (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void createBriefFromSelection()}
                >
                  Create Brief ({selectedSavedAnalysisIds.length})
                </Button>
              )}
            </div>

            {!savedAnalysesAvailable ? (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50/60 p-5 text-sm text-red-900">
                Saved analyses could not be loaded. Try again later.
              </div>
            ) : savedAnalyses.length === 0 ? (
              <div className="mt-4 rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center">
                <p className="text-sm font-medium text-gray-900">No saved analyses yet</p>
                <p className="mt-1 text-xs text-gray-500">Save a chart from the Revenue Intelligence chat to add it here.</p>
              </div>
            ) : (
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {savedAnalyses.map(savedAnalysis => {
                  const selected = selectedSavedAnalysisIds.includes(savedAnalysis.id);
                  return (
                    <article key={savedAnalysis.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                      <div className="flex items-start gap-3">
                        <button
                          type="button"
                          aria-label={selected ? "Remove from Brief" : "Add to Brief"}
                          className={cn(
                            "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border",
                            selected
                              ? "border-[#5E548E] bg-[#5E548E] text-white"
                              : "border-gray-300 text-transparent hover:border-[#9F86C0]",
                          )}
                          onClick={() => {
                            setSelectedSavedAnalysisIds(current => (
                              selected
                                ? current.filter(id => id !== savedAnalysis.id)
                                : current.length < 3
                                  ? [...current, savedAnalysis.id]
                                  : current
                            ));
                          }}
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                        <div className="min-w-0 flex-1">
                          <h3 className="truncate text-sm font-medium text-gray-900">{savedAnalysis.name}</h3>
                          <p className="mt-1 line-clamp-3 text-xs leading-5 text-gray-500">
                            {savedAnalysis.original_instruction}
                          </p>
                          <p className="mt-3 text-[11px] text-gray-400">
                            {savedAnalysis.period_behavior === "relative" ? "Period updates on refresh" : "Fixed period"}
                          </p>
                          <div className="mt-4 flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => void openSavedAnalysis(savedAnalysis)}
                            >
                              Open
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => void openSavedAnalysis(savedAnalysis, true)}
                            >
                              Refresh
                            </Button>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>
    );
  }

  if (!homeMode && collapsed) {
    return (
      <aside className="hidden md:flex md:sticky md:top-0 md:h-screen border-l bg-white">
        <div className="group/chat-toggle flex w-16 flex-col items-center pt-5">
          <div className="relative h-9 w-9">
            <MessageSquare className="absolute inset-0 m-auto h-4 w-4 text-[#231942] transition-opacity group-hover/chat-toggle:opacity-0" />
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onToggleCollapsed}
              title="Expand chat"
              className="absolute inset-0 hidden h-9 w-9 bg-transparent text-[#231942] opacity-0 hover:bg-[#231942]/5 hover:text-[#231942] md:inline-flex group-hover/chat-toggle:opacity-100"
            >
              <PanelRightOpen className="h-4 w-4" />
              <span className="sr-only">Expand chat</span>
            </Button>
          </div>
        </div>
      </aside>
    );
  }

  return (
    <div className={cn(
      homeMode
        ? "col-start-1 row-start-1 z-10 flex min-h-screen min-w-0 flex-col overflow-y-auto bg-gray-50 pt-[60px] md:col-start-2"
        : "hidden flex-col border-l bg-white md:sticky md:top-0 md:flex md:h-screen",
    )}>
      {homeMode ? null : (
      <div className="flex h-14 items-center justify-between border-b px-5 lg:h-[60px]">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#5E548E]/10 text-[#5E548E]">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-gray-900">
                Revenue Intelligence
              </p>
              <Badge
                variant="outline"
                className="h-5 border-[#9F86C0]/40 bg-[#9F86C0]/10 px-1.5 text-[10px] font-medium text-[#5E548E]"
              >
                Beta
              </Badge>
            </div>
            <p className="text-xs text-gray-500">
              Talk to your data
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onToggleCollapsed}
          title="Collapse chat"
        >
          <PanelRightClose className="h-4 w-4" />
          <span className="sr-only">Collapse chat</span>
        </Button>
      </div>
      )}

      {homeMode && (
        <div className="mx-auto mt-7 w-[calc(100%-3rem)] max-w-5xl">
          {renderHomeSignalsSection()}
        </div>
      )}

      <div className={cn(
        "flex min-h-0 flex-1 flex-col",
        homeMode && "mx-auto mt-4 min-h-[300px] w-[calc(100%-3rem)] max-w-5xl rounded-xl border border-gray-200 bg-white",
      )}>
        {availabilityState && (
          <div
            role="status"
            className="border-b border-[#9F86C0]/25 bg-[#9F86C0]/10 px-5 py-2.5 text-[11px] leading-4 text-[#231942]"
          >
            {availabilityState === "support" ? (
              <>
                Advanced analysis is temporarily unavailable. Answers may be simplified. If the problem continues, contact{" "}
                <a className="font-medium underline underline-offset-2" href="mailto:support@revelate.app">
                  support@revelate.app
                </a>
                .
              </>
            ) : getRevenueIntelligenceChatAvailabilityMessage(availabilityState)}
          </div>
        )}
        <div className={cn(
          "flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-5",
          homeMode && "justify-center py-6",
        )}>
          {messages.length === 0 ? (
            <div className={cn(
              "flex flex-col gap-4",
              !homeMode && "flex-1",
            )}>
              <div className="flex flex-col items-center gap-3 text-center">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#5E548E]/10 text-[#5E548E]">
                  <MessageSquare className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xl font-semibold text-gray-900">
                    What do you want to know?
                  </p>
                  <p className="mt-1 text-sm leading-5 text-gray-500">
                    Ask a question, then dig deeper with follow-ups.
                  </p>
                </div>
              </div>
              {!homeMode && (
              <>
              {opening && (
                <button
                  type="button"
                  onClick={() => void submitQuestion(opening.question, opening.context)}
                  className="rounded-xl border border-[#5E548E]/15 bg-[#5E548E]/5 p-3 text-left transition-colors hover:bg-[#5E548E]/10"
                >
                  <span className="text-[10px] font-medium uppercase tracking-wide text-[#5E548E]">
                    {opening.eyebrow}
                  </span>
                  <span className="mt-1 block text-xs font-medium leading-5 text-gray-900">
                    {opening.message}
                  </span>
                  <span className="mt-2 block text-[11px] text-[#5E548E]">
                    Ask why
                  </span>
                </button>
              )}
              {accessNotice && (
                <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-[11px] leading-4 text-amber-900">
                  Answers are limited to data allowed by your role and scope.
                </div>
              )}
              <div className="flex flex-col gap-2">
                {(suggestedQuestions.length ? suggestedQuestions : defaultSuggestedQuestions)
                  .slice(0, 4)
                  .map(question => (
                  <button
                    key={question}
                    type="button"
                    onClick={() => void submitQuestion(question)}
                    className="rounded-full bg-[#5E548E]/10 px-4 py-2 text-left text-xs text-gray-800 transition-colors hover:bg-[#5E548E]/15"
                  >
                    {question}
                  </button>
                  ))}
              </div>
              </>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {messages.map(message => (
                <div key={message.id}>
                  <div
                    className={cn(
                      "rounded-[10px] px-3 py-2 text-xs leading-5",
                      message.role === "user"
                        ? "ml-8 bg-[#231942] text-white"
                        : "mr-8 whitespace-pre-line border border-gray-200 bg-gray-50 text-gray-700",
                    )}
                  >
                    {message.content}
                  </div>
                  {message.role === "assistant" && message.chart?.plan.chart && (
                    <div className="mr-8">
                      <RevenueIntelligenceChatChart
                        autoExpand={!autoExpandedChartMessageIdsRef.current.has(message.id)}
                        chart={message.chart.plan.chart}
                        conclusion={message.chart.conclusion}
                        dataset={message.chart.dataset}
                        language={isRevenueIntelligenceSwedish(message.chart.instruction) ? "sv" : "en"}
                        onAutoExpanded={() => {
                          autoExpandedChartMessageIdsRef.current.add(message.id);
                        }}
                        onSave={async () => {
                          if (message.chart) {
                            return Boolean(await saveChartAnalysis(message.chart));
                          }
                          return false;
                        }}
                        period={message.chart.period}
                      />
                    </div>
                  )}
                  {message.role === "assistant" && message.brief && (
                    <div className="mr-8">
                      <RevenueIntelligenceChatBrief
                        items={message.brief.items}
                        language={message.brief.items.some(item => (
                          isRevenueIntelligenceSwedish(item.plan.chart?.title ?? "")
                        )) ? "sv" : "en"}
                        period={message.brief.period}
                        title={message.brief.title}
                        onPublished={async () => {
                          if (!currentUser?.id || !currentUser.company_id || !message.brief) {
                            return;
                          }
                          try {
                            await saveRevenueIntelligenceBriefSnapshot({
                              briefId: message.brief.briefId,
                              companyId: currentUser.company_id,
                              name: message.brief.title,
                              snapshot: {
                                format: "16:9-png",
                                dataAsOf: new Date().toISOString(),
                                period: message.brief.period,
                                items: message.brief.items,
                                signature: "Intelligence by Revelate",
                              },
                              userId: currentUser.id,
                            });
                            toast({
                              title: "Brief Snapshot saved",
                              description: "The published 16:9 output is preserved.",
                            });
                          } catch {
                            toast({
                              title: "PNG downloaded",
                              description: "The Snapshot could not be stored.",
                              variant: "destructive",
                            });
                          }
                        }}
                      />
                    </div>
                  )}
                  {message.role === "assistant" && message.followUpQuestions && (
                    <div className="mr-8 mt-2 flex flex-wrap gap-1.5">
                      {message.followUpQuestions.slice(0, 3).map(question => (
                        <button
                          key={question}
                          type="button"
                          onClick={() => void submitQuestion(question)}
                          className="rounded-full border border-[#5E548E]/15 bg-white px-2.5 py-1 text-left text-[11px] text-[#5E548E] transition-colors hover:bg-[#5E548E]/5"
                        >
                          {question}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {working && <ChatWorkingIndicator label={activityLabel} />}
            </div>
          )}
        </div>

        <form
          onSubmit={handleSubmit}
          className={cn(
            "border-t",
            homeMode ? "px-5 py-4" : "px-4 py-3",
          )}
        >
          <Input
            value={input}
            onChange={event => setInput(event.target.value)}
            placeholder="Ask about your data..."
            disabled={working || availabilityState === "offline"}
            rightSection={(
              <button
                type="submit"
                disabled={working || availabilityState === "offline"}
                className={cn(
                  "inline-flex items-center justify-center rounded-full bg-[#BE95C4] text-white transition-colors hover:bg-[#9F86C0]",
                  homeMode ? "h-9 w-9" : "h-6 w-6",
                )}
                aria-label="Send question"
              >
                <SendHorizontal className="h-3.5 w-3.5" />
              </button>
            )}
            className={cn(
              "rounded-full border-[#5E548E]/20 bg-[#5E548E]/5",
              homeMode ? "h-14 px-6 pr-14 text-base" : "pr-10",
            )}
          />
          <p className="mt-2 flex items-start gap-1.5 px-1 text-[10px] leading-4 text-gray-500">
            <Info className="mt-0.5 h-3 w-3 shrink-0" />
            Beta answers use available Revelate data. Verify important decisions.
          </p>
        </form>
      </div>
      {homeMode && (
        <div className="mx-auto mb-10 mt-12 flex w-[calc(100%-3rem)] max-w-5xl flex-col gap-4">
          {messages.length === 0 && (
            <div className="grid gap-2 sm:grid-cols-2">
              {(suggestedQuestions.length ? suggestedQuestions : defaultSuggestedQuestions)
                .slice(0, 4)
                .map(question => (
                  <button
                    key={question}
                    type="button"
                    onClick={() => void submitQuestion(question)}
                    className="rounded-full bg-[#5E548E]/10 px-4 py-2 text-left text-xs text-gray-800 transition-colors hover:bg-[#5E548E]/15"
                  >
                    {question}
                  </button>
                ))}
            </div>
          )}

          {accessNotice && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-[11px] leading-4 text-amber-900">
              Answers are limited to data allowed by your role and scope.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
